// Starting a rung against the server. One-click is an approval the executor runs (followed by
// SSE, like any approved action); mailto hands back a compose draft the owner sends through the
// compose sheet; link returns the URL for the owner's own browser. After any rung the server
// names the screen-out call the owner may make next; it is offered, never made here.
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { UnsubscribeResult, UnsubscribeRung, UnsubscribeView } from "@shared/api";
import { isApiError } from "@web/lib/api";
import { invalidateCompose } from "@web/lib/composeQueries";
import { invalidateBoards } from "@web/lib/feedQueries";
import { invalidateSenders } from "@web/lib/sendersQueries";
import { unsubscribeApi } from "@web/lib/unsubscribeApi";
import { acceptUnsubscribeView, invalidateUnsubscribe } from "@web/lib/unsubscribeQueries";
import { composeStore } from "@web/compose/composeStore";

export interface ClimbOutcome {
  result: UnsubscribeResult;
  /** The link rung: the URL when the browser refused to open a new tab (popup blocked). */
  blockedUrl: string | null;
}

function settle(queryClient: QueryClient, view: UnsubscribeView) {
  acceptUnsubscribeView(queryClient, view);
  invalidateUnsubscribe(queryClient);
  invalidateSenders(queryClient);
}

/** Opens `url` in a new tab without handing it the opener; null when the browser refused. */
function openInNewTab(url: string): boolean {
  if (!/^https:\/\//i.test(url)) return false;
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  return opened !== null;
}

/**
 * Start one rung the owner chose. Returns null (after a toast) when the server refused; the
 * one-click rung must already have been confirmed by the caller.
 */
export async function climbRung(
  queryClient: QueryClient,
  view: Pick<UnsubscribeView, "accountId" | "address" | "name" | "methods">,
  rung: UnsubscribeRung,
  fingerprint?: string,
): Promise<ClimbOutcome | null> {
  let result: UnsubscribeResult;
  try {
    result = await unsubscribeApi.climb(view.accountId, view.address, rung, fingerprint);
  } catch (error) {
    if (isApiError(error, "invalid_request"))
      toast(error.serverMessage ?? "This sender doesn't offer that way to unsubscribe.");
    else if (isApiError(error) && error.status === 409)
      toast(error.serverMessage ?? "An earlier request is still running. Wait for it.");
    else if (isApiError(error) && error.status === 404)
      toast("MeeMail doesn't know this sender yet.");
    else toast("Couldn't start that just now. Nothing was sent.");
    invalidateUnsubscribe(queryClient);
    void queryClient.invalidateQueries({
      queryKey: ["unsubscribePreview", view.accountId, view.address.toLowerCase()],
    });
    return null;
  }
  settle(queryClient, result.view);
  switch (result.kind) {
    case "approval": {
      invalidateBoards(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["work"] });
      toast(`Unsubscribe request queued for ${view.methods.oneClick?.host ?? "their server"}.`, {
        description: "One request, to the address you confirmed. Nothing is emailed.",
      });
      return { result, blockedUrl: null };
    }
    case "draft": {
      invalidateCompose(queryClient);
      composeStore.open({ accountId: result.draft.accountId, draftId: result.draft.id });
      toast("A draft is ready. Send it to ask by email; you can undo for a few seconds.");
      return { result, blockedUrl: null };
    }
    case "link": {
      const opened = openInNewTab(result.url);
      toast(opened ? "Opened their page in a new tab." : "Your browser blocked the new tab.");
      return { result, blockedUrl: opened ? null : result.url };
    }
  }
}
